import { t } from '@lingui/macro'
import { Form, Formik } from 'formik'
import { isNil } from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { useTheme } from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { AddressAutocomplete } from '@/@bothrs-ui/web/components/forms/addressAutocomplete'
import { Dropdown } from '@/@bothrs-ui/web/components/forms/dropdown/Dropdown'
import { FormListener } from '@/@bothrs-ui/web/components/forms/FormListener'
import { Input } from '@/@bothrs-ui/web/components/forms/input'
import { Label } from '@/@bothrs-ui/web/components/forms/label'
import { FlexColumn, Margin } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { formatPhoneNumber } from '@/utils/format-phone-number'
import { useKinshipTypes } from '../../hooks/useKinshipTypes'
import { generateAddContactFormSchema } from './AddContactForm.schema'
import { ScrollableContainer } from './AddContactForm.styled'

import type { Path } from 'ramda'
import type { AdditionalContact } from '@/modules/onboarding/pages/additional-contact-information/components/AddContactsBox'

type AddContactFormProps = {
  orderIndex?: number
  initialValues?: Partial<AdditionalContact>
  actionLabel: string
  setFormValid?: React.Dispatch<React.SetStateAction<boolean>>
  onComplete: (contact: AdditionalContact) => void
}

const AddContactForm = ({ orderIndex, initialValues, actionLabel, setFormValid, onComplete }: AddContactFormProps) => {
  const theme = useTheme()
  const {
    getKinshipTypes: { execute, value: kinshipTypes },
  } = useKinshipTypes()

  const AddContactFormSchema = generateAddContactFormSchema()

  useEffect(() => {
    execute()
  }, [])

  const [postalCodeAndCityMismatch, setPostalCodeAndCityMismatch] = useState<boolean>(false)

  const [contactOptionsTouched, setContactOptionsTouched] = useState(false)

  const ignoreContactOptions = useMemo(() => {
    return contactOptionsTouched
  }, [contactOptionsTouched])

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  function handleSubmit(values: Record<string, any>) {
    onComplete({
      id: values.id,
      healthblocksId: values.healthblocksId,
      order: orderIndex ?? 0,
      kinshipId: values.kinshipId,
      givenName: values.givenName,
      familyName: values.familyName,
      primaryPhoneNumber: values.primaryPhoneNumber,
      additionalPhoneNumber: values.additionalPhoneNumber,
      email: values.email,
      address: values.address,
    })
  }

  function handleFormChange(_values: Record<string, unknown>, isValid: boolean) {
    if (setFormValid) {
      setFormValid(isValid)
    }
  }

  // Template variables
  // ------------------------------------------------------------------------- /
  const formInitialValues = useMemo(() => {
    return {
      id: initialValues?.id,
      healthblocksId: initialValues?.healthblocksId || uuidv4(),
      order: orderIndex,
      kinshipId: isNil(initialValues?.kinshipId) || initialValues?.kinshipId === '0' ? '' : initialValues?.kinshipId,
      givenName: initialValues?.givenName || '',
      familyName: initialValues?.familyName || '',
      email: initialValues?.email || '',
      primaryPhoneNumber: formatPhoneNumber(initialValues?.primaryPhoneNumber),
      additionalPhoneNumber: formatPhoneNumber(initialValues?.additionalPhoneNumber),
      address: {
        streetAddress: initialValues?.address?.streetAddress || '',
        city: initialValues?.address?.city || '',
        zipOrPostalCode: initialValues?.address?.zipOrPostalCode || '',
        country: initialValues?.address?.country || 'BE',
      },
    }
  }, [initialValues, orderIndex])

  return (
    <ScrollableContainer>
      <Formik initialValues={formInitialValues} validationSchema={AddContactFormSchema} onSubmit={handleSubmit}>
        {({ values, errors, dirty, isValid }) => {
          return (
            <Form id='add-contact-form'>
              <FormListener
                fields={Object.keys(values) as unknown as Path[]}
                errors={errors}
                onChange={handleFormChange}
              />

              <FlexColumn gap={theme.UI.Spacing.Space4}>
                <Input name='id' type='hidden' />
                <Input name='healthblocksId' type='hidden' />
                <Input name='order' type='hidden' />

                <Input
                  label={t({
                    id: 'globals.labels.givenName',
                    message: 'Voornaam',
                  })}
                  name='givenName'
                  type='text'
                  placeholder='Voornaam'
                  required
                  noMargin
                  maxLength={100}
                  ignoreTouched={false}
                />

                <Input
                  label={t({
                    id: 'globals.labels.familyName',
                    message: 'Familienaam',
                  })}
                  name='familyName'
                  type='text'
                  placeholder='Familienaam'
                  required
                  noMargin
                  maxLength={100}
                  ignoreTouched={false}
                />

                <Dropdown
                  label={t({
                    id: 'globals.labels.kinship',
                    message: 'Verwantschap',
                  })}
                  name='kinshipId'
                  placeholder={t({
                    id: 'globals.placeholder.kinship',
                    message: 'Kies een verwantschap',
                  })}
                  options={kinshipTypes || [{ value: '', label: 'Laden...' }]}
                  disabled={!!initialValues?.kinshipId}
                  required
                  noMargin
                  ignoreTouched={false}
                />

                <AddressAutocomplete
                  id='address'
                  label={t({
                    id: 'globals.labels.address',
                    message: 'Adres',
                  })}
                  placeholder={t({
                    id: 'globals.labels.addresautocomplete',
                    message: 'Zoek je adres',
                  })}
                  required
                  ignoreTouched={false}
                  setPostalCodeAndCityMismatch={setPostalCodeAndCityMismatch}
                />

                <Label color={theme.Colors.Neutrals.Darker} type='BodyMediumBold'>
                  {t({
                    id: 'globals.labels.contactoptions',
                    message: 'Contactopties (minstens 1) *',
                  })}
                </Label>

                <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest} style={{ fontWeight: 400 }}>
                  {t({
                    id: 'globals.labels.email',
                    message: 'E-mailadres',
                  })}
                </Span>
                <Input
                  name='email'
                  type='email'
                  inputMode='email'
                  placeholder='voornaam.achternaam@email.be'
                  autoComplete='email'
                  noMargin
                  ignoreTouched={ignoreContactOptions}
                  onBlur={() => {
                    setContactOptionsTouched(true)
                  }}
                />

                <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest} style={{ fontWeight: 400 }}>
                  {t({
                    id: 'globals.labels.primary-phone',
                    message: 'Telefoonnummer (vast)',
                  })}
                </Span>
                <Input
                  name='primaryPhoneNumber'
                  type='tel'
                  inputMode='tel'
                  placeholder='+32 0 000 00 00'
                  autoComplete='tel'
                  noMargin
                  ignoreTouched={ignoreContactOptions}
                  onBlur={() => {
                    setContactOptionsTouched(true)
                  }}
                />

                <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest} style={{ fontWeight: 400 }}>
                  {t({
                    id: 'globals.labels.extra-phone',
                    message: 'Telefoonnummer (mobiel)',
                  })}
                </Span>
                <Input
                  name='additionalPhoneNumber'
                  type='tel'
                  inputMode='tel'
                  placeholder='+32 000 00 00 00'
                  autoComplete='tel'
                  noMargin
                  ignoreTouched={ignoreContactOptions}
                  onBlur={() => {
                    setContactOptionsTouched(true)
                  }}
                />
              </FlexColumn>

              <Margin mt={theme.UI.Spacing.Space8}>
                <Button
                  variant='Primary'
                  type='submit'
                  prefixIcon={['fal', 'check']}
                  disabled={!isValid || !dirty || postalCodeAndCityMismatch}
                >
                  {actionLabel}
                </Button>
              </Margin>
            </Form>
          )
        }}
      </Formik>
    </ScrollableContainer>
  )
}

export { AddContactForm }
